// The exit of a page: its modules fade out in one fixed rhythm while the next
// page's background comes up over it.
//
//   - the section on screen (or the home stage): every visible module fades,
//     top to bottom then left to right, each STEP after the previous one
//   - other sections still on screen fade as a whole in the same rhythm;
//     anything off screen goes at once
//   - a carousel: one left-to-right sweep (desktop) or all panels at once (phone)
//   - the next page's background gradient comes up on a canvas over the element
//     (a canvas, not a coloured layer, so mobile Safari keeps the page's colour
//     for its bars); onCovered fires once it fully covers the screen
//
// Ported from the "plain" exit of the transitions.dev UI-generation script that
// the Framer site used; the drawing parts of that script were never shown and
// are gone.

const STEP = 120 // ms between modules
const MAX = 720 // latest start of the last module (long lists get squeezed)
const FADE = 380 // ms per module
const SWEEP = 420 // carousel: left edge to right edge
const MARGIN = 44 // the cover reaches this far past the element
const MAX_PARTS = 45

const clamp = (x: number) => Math.max(0, Math.min(1, x))
const smooth = (a: number, b: number, x: number) => {
    const q = clamp((x - a) / (b - a))
    return q * q * (3 - 2 * q)
}
const inOut = (k: number) => (k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2)
const alpha = (c: string) => {
    const m = String(c).match(/rgba?\(([^)]+)\)/)
    if (!m) return c && c !== "transparent" ? 1 : 0
    const p = m[1].split(/[ ,/]+/).filter(Boolean)
    return p.length > 3 ? +p[3] : 1
}

/**
 * What fades: the outermost "visible things" inside el, i.e. boxes (a fill, a
 * border or outline, a background image, an image or video, a control) and
 * elements holding text. Hidden and full-size wrappers are looked through.
 */
function modulesOf(el: HTMLElement): HTMLElement[] {
    const R = el.getBoundingClientRect()
    const area = R.width * R.height
    type Part = { el?: HTMLElement; box?: boolean; x: number; y: number; w: number; h: number }
    const parts: Part[] = []
    const hidden = (n: Element) => {
        for (let x: Element | null = n; x && x !== el; x = x.parentElement) {
            const cs = getComputedStyle(x)
            if (cs.display === "none" || cs.visibility === "hidden" || +cs.opacity < 0.05) return true
        }
        return false
    }
    const walk = (node: Element) => {
        for (const c of Array.from(node.children) as HTMLElement[]) {
            if (c.matches("script, style, template, .sh") || hidden(c)) continue
            const r = c.getBoundingClientRect(),
                cs = getComputedStyle(c)
            if (!r.width || !r.height) {
                walk(c)
                continue
            }
            const rel = { x: r.left - R.left, y: r.top - R.top, w: r.width, h: r.height }
            if (c instanceof SVGSVGElement) {
                // small icons count (they take a slot in the parts limit) but don't fade on their own
                if (r.width <= 120 && r.height <= 120 && c.querySelector("path, circle, ellipse, rect, line, polyline, polygon")) parts.push(rel)
                continue
            }
            const big = r.width * r.height > area * 0.6
            const boxy =
                !big &&
                !/blur/.test(cs.filter) &&
                (alpha(cs.backgroundColor) > 0.02 ||
                    parseFloat(cs.borderTopWidth) > 0 ||
                    c.getAttribute("data-block") === "outline" ||
                    /^(IMG|CANVAS|VIDEO|INPUT|BUTTON|TEXTAREA|SELECT)$/.test(c.tagName) ||
                    cs.backgroundImage !== "none")
            if (boxy) parts.push({ el: c, box: true, ...rel })
            if (Array.from(c.childNodes).some((n) => n.nodeType === 3 && n.nodeValue!.trim())) parts.push({ el: c, ...rel })
            walk(c)
        }
    }
    walk(el)
    // the same box twice (a button and the span that fills it) counts once
    for (let i = parts.length - 1; i > 0; i--) {
        const p = parts[i]
        if (!p.box) continue
        if (parts.some((q, j) => j < i && q.box && Math.abs(q.x - p.x) < 1.5 && Math.abs(q.y - p.y) < 1.5 && Math.abs(q.w - p.w) < 1.5 && Math.abs(q.h - p.h) < 1.5))
            parts.splice(i, 1)
    }
    if (parts.length > MAX_PARTS) {
        const keep = new Set([...parts].sort((a, b) => b.w * b.h - a.w * a.h).slice(0, MAX_PARTS))
        for (let i = parts.length - 1; i >= 0; i--) if (!keep.has(parts[i])) parts.splice(i, 1)
    }
    const traced = new Set(parts.filter((p) => p.el && p.el !== el).map((p) => p.el!))
    const out = new Set<HTMLElement>()
    for (const t of traced) {
        let top = t
        for (let x = t.parentElement; x && x !== el; x = x.parentElement) if (traced.has(x as HTMLElement)) top = x as HTMLElement
        out.add(top)
    }
    return [...out]
}

export type ExitOptions = {
    duration: number
    /** the next page's background: [r, g, b, position 0..1] stops, top to bottom */
    stops: number[][]
    /** other sections still on screen */
    also?: HTMLElement[]
    /** carousel track: desktop sweep */
    sweep?: HTMLElement | null
    /** carousel track: phone, all panels at once */
    whole?: HTMLElement | null
    /** every frame: dark = how far the next page's background is up (0..1);
     *  ahead = the same a moment later, for what shows it late (Safari's bars) */
    onDark?: (dark: number, ahead: number) => void
    onCovered?: () => void
}
export type ExitHandle = { layer: HTMLElement; finished: Promise<void>; remove: () => void }

/** Drop finished entrances that hold a value (fill: forwards), keeping it. */
function settle(el: HTMLElement) {
    for (const a of el.getAnimations()) {
        try {
            a.commitStyles()
        } catch {}
        a.cancel()
    }
}

export function exitPage(el: HTMLElement, o: ExitOptions): ExitHandle {
    const vh = innerHeight,
        vw = innerWidth
    const R = el.getBoundingClientRect()

    // ----- the modules -----
    const targets = [...modulesOf(el), ...(o.also || [])].map((t) => ({ el: t, o: t.style.opacity }))
    const seq: { el: HTMLElement; r: DOMRect }[] = []
    for (const tg of targets) {
        tg.el.style.transition = "none"
        if (o.sweep?.contains(tg.el) || o.whole?.contains(tg.el)) continue // handled as a whole
        const r = tg.el.getBoundingClientRect()
        // on screen and actually seen (not hidden, transparent or collapsed)
        const seen = (() => {
            if (r.width < 2 || r.height < 2) return false
            if (tg.o !== "" && tg.o != null && +tg.o === 0) return false
            const ck = (tg.el as any).checkVisibility
            if (ck && !ck.call(tg.el, { opacityProperty: true, visibilityProperty: true })) return false
            for (let e: HTMLElement | null = tg.el; e && e !== document.body; e = e.parentElement) {
                const cs = getComputedStyle(e)
                if (cs.display === "none" || cs.visibility === "hidden" || +cs.opacity === 0) return false
            }
            return true
        })()
        if (!(seen && r.bottom > 0 && r.top < vh && r.right > 0 && r.left < vw)) {
            tg.el.style.opacity = "0"
            continue
        }
        seq.push({ el: tg.el, r })
    }
    const all = seq.filter((a) => !seq.some((b) => b !== a && b.el.contains(a.el)))
    const swept = o.sweep ? all.filter((a) => o.sweep!.contains(a.el)) : []
    const order = all.filter((a) => !swept.includes(a) && !(o.whole && o.whole.contains(a.el)))
    if (o.whole) {
        const wr = o.whole.getBoundingClientRect()
        if (wr.bottom > 0 && wr.top < vh && wr.width > 0) order.push({ el: o.whole, r: wr })
    }
    order.sort((a, b) => (Math.abs(a.r.top - b.r.top) > 24 ? a.r.top - b.r.top : a.r.left - b.r.left))
    const n = order.length
    const step = n > 1 ? Math.min(STEP, MAX / (n - 1)) : 0
    order.forEach(({ el }) => settle(el))
    swept.forEach(({ el }) => settle(el))
    if (order.length) void getComputedStyle(order[0].el).opacity // fades start from the held value
    const curve = "cubic-bezier(.4,0,.2,1)"
    if (o.sweep) {
        // every panel fades through filter: opacity(), which the carousel never
        // writes (it sets each panel's opacity on every frame)
        const items = (Array.from(o.sweep.children) as HTMLElement[]).map((c) => ({ el: c, r: c.getBoundingClientRect() }))
        const xs = items.map((a) => Math.max(0, Math.min(vw, a.r.left + a.r.width / 2)))
        const x0 = Math.min(...xs),
            x1 = Math.max(...xs)
        if (items.length) void getComputedStyle(items[0].el).filter
        requestAnimationFrame(() =>
            items.forEach((a, i) => {
                const k = x1 > x0 ? (xs[i] - x0) / (x1 - x0) : 0
                a.el.style.transition = `filter ${FADE}ms ${curve} ${Math.round(k * SWEEP)}ms`
                a.el.style.filter = "opacity(0)"
            })
        )
    }
    requestAnimationFrame(() =>
        order.forEach(({ el: t }, i) => {
            const delay = Math.round(i * step)
            // a 3D carousel can't fade as a whole (mobile Safari flattens it below
            // full opacity): its panels fade together instead
            if (o.whole && t === o.whole) {
                ;(Array.from(o.whole.children) as HTMLElement[]).forEach((c) => {
                    c.style.transition = `filter ${FADE}ms ${curve} ${delay}ms`
                    c.style.filter = "opacity(0)"
                })
                return
            }
            t.style.transition = `opacity ${FADE}ms ${curve} ${delay}ms`
            t.style.opacity = "0"
        })
    )

    // ----- the next page's background, on a canvas over the element -----
    const layer = document.createElement("div")
    layer.className = "x-cover"
    layer.setAttribute("aria-hidden", "true")
    Object.assign(layer.style, { left: `${R.left}px`, top: `${R.top}px`, width: `${R.width}px`, height: `${R.height}px` })
    const dpr = Math.min(2, devicePixelRatio || 1)
    const cv = document.createElement("canvas")
    cv.width = Math.ceil((R.width + 2 * MARGIN) * dpr)
    cv.height = Math.ceil((R.height + 2 * MARGIN) * dpr)
    Object.assign(cv.style, {
        position: "absolute",
        left: -MARGIN + "px",
        top: -MARGIN + "px",
        width: R.width + 2 * MARGIN + "px",
        height: R.height + 2 * MARGIN + "px",
    })
    layer.appendChild(cv)
    document.body.appendChild(layer)
    const cx = cv.getContext("2d")!
    // the gradient spans the screen (the canvas starts MARGIN above the element)
    const g = cx.createLinearGradient(0, (0 - (R.top - MARGIN)) * dpr, 0, (vh - (R.top - MARGIN)) * dpr)
    for (const s of o.stops) g.addColorStop(clamp(s[3]), `rgb(${s[0]},${s[1]},${s[2]})`)
    layer.style.opacity = "0"
    layer.style.transition = "opacity 360ms cubic-bezier(.5,0,.5,1)"
    requestAnimationFrame(() => (layer.style.opacity = "1"))

    const ms = Math.max(400, o.duration)
    const FROM = 0.74,
        TO = 0.02
    let raf = 0,
        stopped = false,
        covered = false,
        resolve!: () => void
    const finished = new Promise<void>((r) => (resolve = r))
    const t0 = performance.now()
    const darkAt = (t: number) => 1 - smooth(0.08, 0.4, FROM - (FROM - TO) * inOut(Math.min(1, Math.max(0, (t - t0) / ms))))
    // Safari shows a new bar colour by fading to it from the last one over
    // 100ms, restarted on every change from the colour it was last given
    // (WebKit WKColorExtensionView): during a page change the bars trail the
    // page by one frame. They're handed the colour one frame ahead, so what
    // shows lines up (simulated: off by at most 2/255 instead of 15/255).
    let last = 0,
        frame = 1000 / 60
    const tick = (now: number) => {
        if (stopped) return
        if (last) frame += (Math.min(50, Math.max(4, now - last)) - frame) * 0.2
        last = now
        const k = Math.min(1, (now - t0) / ms)
        const dark = darkAt(now)
        o.onDark?.(dark, darkAt(now + frame))
        // keep over the element if the page moves underneath (until it's gone)
        if (el.isConnected) {
            const er = el.getBoundingClientRect()
            layer.style.transform = `translate3d(${(er.left - R.left).toFixed(2)}px, ${(er.top - R.top).toFixed(2)}px, 0)`
        }
        cx.clearRect(0, 0, cv.width, cv.height)
        if (dark > 0) {
            cx.globalAlpha = dark
            cx.fillStyle = g
            cx.fillRect(0, 0, cv.width, cv.height)
            cx.globalAlpha = 1
        }
        if (!covered && dark >= 0.985) {
            covered = true
            o.onCovered?.()
        }
        if (k < 1) raf = requestAnimationFrame(tick)
        else resolve()
    }
    raf = requestAnimationFrame(tick)
    return {
        layer,
        finished,
        remove() {
            stopped = true
            cancelAnimationFrame(raf)
            layer.remove()
        },
    }
}
